import RenderRichTextContent from '@components/common/render-rich-text-content'
import {
  ANSWER_BUTTON_COLOR,
  OPTION_BUTTON_BG_COLORS,
} from '@lib/constants/quizzes'
import { ProblemType } from '@lib/types/quizzes'
import { useSpring } from '@react-spring/core'
import { animated } from '@react-spring/web'
import classNames from 'classnames'
import { useEffect, useMemo } from 'react'
import { Check } from 'react-feather'

type OptionButtonProps = {
  buttonBgColor: typeof OPTION_BUTTON_BG_COLORS[number]
  content: string
  onClick: () => void
  keyupTriggerKey: string
  isSelected: boolean
  isLoading: boolean
  isAnswerValidated: boolean
  isCorrect: boolean
  problemType: ProblemType
}

const OptionButton = ({
  buttonBgColor,
  content,
  onClick,
  keyupTriggerKey,
  isSelected,
  isLoading,
  isAnswerValidated,
  isCorrect,
  problemType,
}: OptionButtonProps) => {
  useEffect(() => {
    const handleKeyup = (e: KeyboardEvent) => {
      e.key === keyupTriggerKey && onClick()
    }
    window.addEventListener('keyup', handleKeyup)
    return () => window.removeEventListener('keyup', handleKeyup)
  }, [])

  const loadingStyle = useSpring({
    width: isSelected && isLoading ? '100%' : '0%',
    config: { duration: 10000 },
    pause: !isLoading,
  })

  const buttonColor = useMemo(() => {
    if (!isAnswerValidated) return buttonBgColor
    else if (isCorrect) return ANSWER_BUTTON_COLOR.correct
    else if (isSelected) return ANSWER_BUTTON_COLOR.wrong
    else return buttonBgColor
  }, [buttonBgColor, isAnswerValidated, isCorrect, isSelected])

  return (
    <div
      className={classNames('duration-500 bg-white rounded-lg flex', {
        'opacity-0': isAnswerValidated && !isSelected && !isCorrect,
      })}
      style={{
        border: `2px solid ${buttonColor}`,
      }}
    >
      <div
        style={{
          backgroundColor: buttonColor,
        }}
        className={classNames(
          'flex items-center relative justify-center w-full duration-300',
          {
            'hover:opacity-75 cursor-pointer': !isLoading && !isAnswerValidated,
          }
        )}
        onClick={onClick}
      >
        <p
          className={classNames(
            'absolute top-0 right-0 m-2 w-8 h-8 flex justify-center items-center rounded-md',
            {
              'bg-white':
                (isAnswerValidated || isSelected) && problemType === 'Checkbox',
            }
          )}
          style={{ border: '2px solid #00000033' }}
        >
          {isSelected && problemType === 'Checkbox' ? (
            <Check className="text-grey-800" width={30} />
          ) : (
            keyupTriggerKey
          )}
        </p>
        <div className="overflow-auto thin-scroll-bar mx-2 lg:mx-5 h-full flex items-center">
          <RenderRichTextContent
            content={content}
            className="font-600 leading-tight text-xl lg:text-2xl m-auto"
          />
        </div>
        {isSelected && isLoading && (
          <animated.div
            style={{ ...loadingStyle }}
            className="absolute h-full bg-black rounded-lg opacity-25 left-0"
          />
        )}
      </div>
    </div>
  )
}

export default OptionButton
